/**
 * Created by neo on 24.12.22.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { Button } from '../../Components/Button';
import { buildUrl, downloadBlob, ICalEvent, isIOSSafari } from './utils';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useRootStore } from '../../Store/useRootStore';
import { useLocalized } from '../../Hooks/useLocalized';
import { ButtonProps } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { SubscribableEntryUnion } from '../../Model/Explore/SubscribableEntry';
import { WebinarEntry } from '../../Model/Explore/WebinarEntry';

function convertToIcalEvent(
  title: string,
  description: string,
  entry: SubscribableEntryUnion,
  pageLink?: string,
): ICalEvent {
  const startDate = entry.nextDate;
  const endDate = startDate?.add(entry.dayjsDuration);

  const meetingLink: string | undefined = 'meetingLink' in entry ? (entry.meetingLink as string) : undefined;

  return {
    title,
    description,
    startTime: startDate?.toISOString() ?? '',
    endTime: endDate?.toISOString() ?? '',
    url: pageLink || meetingLink,
    location: pageLink || meetingLink,
    // url: `https://coach.kinastic.com/academy/${entry.id}`,
    // location: `https://coach.kinastic.com/academy/${entry.id}`,
  };
}

export type AddToCalendarButtonProps = Omit<ButtonProps, 'children' | 'value'> & {
  entry: SubscribableEntryUnion;
  rawContent?: string;
};

export const AddToCalendarButton: React.FC<AddToCalendarButtonProps> = observer(
  ({ entry, rawContent = '', ...moreProps }) => {
    const { language, analytics } = useRootStore();
    const { t } = useTrans();
    const entryName = useLocalized(entry.name);
    const entryShortDescription = useLocalized(entry.shortDescription);

    const isCrappyIE = !!(typeof window !== 'undefined' && (window.navigator as any).msSaveOrOpenBlob && window.Blob);

    const handleClick = React.useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();

        const filename = `${entryName}.ics`;
        const url: string = buildUrl(
          convertToIcalEvent(entryName, entryShortDescription, entry, window.location.href),
          isIOSSafari(),
          rawContent,
        );
        const blob: Blob = new Blob([url], {
          type: 'text/calendar;charset=utf-8',
        });

        // IE
        if (isCrappyIE) {
          (window.navigator as any).msSaveOrOpenBlob(blob, filename);
          return;
        }

        // Safari
        if (isIOSSafari()) {
          window.open(url, '_blank');
          return;
        }

        // Desktop
        downloadBlob(blob, filename);

        const eventUrl = entry.type === 'onlineMeeting' && 'meetingLink' in entry ? (entry.meetingLink as string) : '';
        analytics.logEvent('webinar_calendar_downloaded', {
          objectId: entry.id,
          objectType: entry.type,
          url: eventUrl,
        });
      },
      [entry, language, rawContent, isCrappyIE, analytics, entryName, entryShortDescription],
    );

    return (
      <Button color="secondary" onClick={handleClick} {...moreProps}>
        <i className="bi bi-calendar-event"></i>&nbsp;
        {t('globals.addToCalendarButton', { defaultValue: 'Add to calendar' })}
      </Button>
    );
  },
);
